import moment from 'moment-timezone'
import { Maybe } from 'types/graphqlTypes'
import { FORMATS, TFormatKey, getFormat } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

type TStringFormat = Exclude<TFormatKey, 'hours' | 'minutes' | 'weekdaysList' | 'fullWeekList'>

/**
 * Formats date into a string
 * @param {string} date i.e. '2011-10-05T14:48:00.000Z'
 * @param {string} [format = null] i.e. 'iso8601Format'
 * @example formatDate(date, 'datetimeShort12H') // '8/11 12:00am EST'
 */
export function formatDate<F extends TStringFormat>(
  date: Maybe<Date | string>,
  format: F | null = null,
  timezone?: string
): string {
  if (!date) {
    return ''
  }
  const dateObj = moment.tz(date, timezone || FORMATS.defaultTimeZone)
  const dateString = dateObj.format(FORMATS.readableFormat)
  const militaryFormat = dateObj.format(FORMATS.militaryTimeFormat)
  const timeString = militaryFormat === '00:00' ? 'Anytime' : militaryFormat
  return format
    ? moment.tz(date, timezone || FORMATS.defaultTimeZone).format(getFormat(format))
    : `${dateString} \u00b7 ${timeString}`
}

/**
 * @example formatUTCDate('1597104000') // '08/11/2020'
 */
export const formatUTCDate = (date: Maybe<string>): string => {
  if (!date) {
    return ''
  }
  return date && moment.utc(date, FORMATS.dateFormat).format(FORMATS.dateFormat)
}

/**
 * @note These two functions should maintain the users's timezone
 * @example formatUTCDateForAPI('08/11/2020') // '2020-11-08T08:02:17-05:00'
 */
export const formatUTCDateForAPI = (date: Maybe<string>): string => {
  if (!date) {
    return ''
  }

  return date && moment(date, FORMATS.dateFormat).toISOString()
}

/**
 * @example formatUTCDateForDateInput('2020-11-08T08:02:17-05:00') // '08/11/2020'
 */
export const formatUTCDateForDateInput = (date: Maybe<string>): string => {
  if (!date) {
    return ''
  }

  return date && moment(date).local().format(FORMATS.dateFormat)
}

/**
 * @example formatDateForApi('2020-11-08T08:02:17-05:00') // '08/11/2020'
 * @example formatDateForApi(moment().subtract(2, 'days')) // '08/09/2020'
 */
export const formatDateForApi = (date: Maybe<string | moment.Moment>): string => {
  if (!date) {
    return ''
  }
  return moment.tz(date, FORMATS.defaultTimeZone).format(FORMATS.dateFormat)
}

/**
 * @example timeSinceShort('1597104000') // '2 days ago'
 */
export const timeSinceShort = (
  timeStamp: string,
  timezone: string = FORMATS.defaultTimeZone
): string => {
  const dateTime = moment.tz(timeStamp, timezone)
  const humanText = dateTime.fromNow()
  return `${humanText}`
}

const DATE_ABBREVIATIONS = {
  year: 'y',
  month: 'mo',
  day: 'd',
  hour: 'h',
  minute: 'min',
  second: 's',
}

type TDateAbbreviationKey = keyof typeof DATE_ABBREVIATIONS

/**
 * @example getTimeAgoShort('1597104000') // '2d ago'
 */
export const getTimeAgoShort = (
  timeStamp: Maybe<string>,
  customMapping: Partial<Record<TDateAbbreviationKey, string>> = {}
): string => {
  if (!timeStamp) {
    return ''
  }
  const mapping = { ...DATE_ABBREVIATIONS, ...customMapping }
  const lastTouchedData = moment.duration(moment().diff(timeStamp))
  const { days, hours, minutes, months, seconds, years } = (lastTouchedData as any)._data
  if (years > 0) return `${years}${mapping.year} ago`
  if (months > 0) return `${months}${mapping.month} ago`
  if (days > 0) return `${days}${mapping.day} ago`
  if (hours > 0) return `${hours}${mapping.hour} ago`
  if (minutes > 0) return `${minutes}${mapping.minute} ago`
  return `${seconds < 0 ? 0 : seconds}${mapping.second} ago`
}

/**
 * @example formatDriverLastPing('America/New_York', '1597104000') //  '2 days ago'
 */
export const formatDriverLastPing = (timezone: Maybe<string>, timeStamp: Maybe<string>): string => {
  if (timezone && timeStamp) {
    return timeSinceShort(timeStamp, timezone)
  }
  return ''
}

/**
 * @example formatActivityDateTime('2020-11-08T08:02:17-05:00') // 'Tomorrow 14:21'
 */
export const formatActivityDateTime = (date: Maybe<string>): string => {
  if (!date) {
    return ''
  }

  return moment.tz(date, FORMATS.defaultTimeZone).calendar(null, {
    nextDay: `[Tomorrow] ${FORMATS.militaryTimeFormat}`,
    nextWeek: `[Next] dddd ${FORMATS.militaryTimeFormat}`,
    lastWeek: `[Last] dddd ${FORMATS.militaryTimeFormat}`,
    lastDay: `[Yesterday] ${FORMATS.militaryTimeFormat}`,
    sameDay: `[Today] ${FORMATS.militaryTimeFormat}`,
    sameElse: `${FORMATS.dateFormat} ${FORMATS.militaryTimeFormat}`,
  })
}

/**
 * @example formatLogsTimestamp('1597104000') // '08/11/2020 00:00'
 */
export const formatLogsTimestamp = (occurredAt: Maybe<string>): string => {
  if (!occurredAt) {
    return ''
  }
  const datetimeChanged = moment.utc(occurredAt, 'YYYY-MM-DD HH:mm z')
  return datetimeChanged.clone().tz(FORMATS.defaultTimeZone).format(FORMATS.datetimeFormat)
}

/**
 * @example minutesLeft(20, '1597104000') // 10
 */
export const minutesLeft = (limitMinutes: number, timestamp: string): number => {
  return limitMinutes - moment().diff(timestamp, 'm')
}

// utils for showing hour or minute labels
const MINUTE_LABEL = 'mins'
const HOUR_LABEL = 'hours'
const ONE_HOUR_LABEL = `1 hour`

/**
 * @example getMinutesLabel(20) // '20 mins'
 */
export const getMinutesLabel = (minutes: number) => {
  return minutes % 60 !== 0 ? ` ${minutes % 60} ${MINUTE_LABEL}` : ''
}

/**
 * @example getMoreThanHourLabel(20) // '0 hours 20 minutes'
 */
export const getMoreThanHourLabel = (minutes: number) => {
  return `${Math.floor(minutes / 60)} ${HOUR_LABEL}${getMinutesLabel(minutes)}`
}

/**
 * @example getLessThanHourLabel(20) // '20 minutes'
 */
export const getLessThanHourLabel = (minutes: number) => {
  return `${minutes} ${MINUTE_LABEL}`
}

/**
 * @example getTimeLabel(20) // '20 minutes'
 * @example getTimeLabel('152')) // '2 hours 32 minutes'
 */
export const getTimeLabel = (minutes: Maybe<number>): string => {
  let label: string
  if (!minutes) {
    label = DISPLAY.empty
  } else if (minutes > 60) {
    label = getMoreThanHourLabel(minutes)
  } else if (minutes === 60) {
    label = ONE_HOUR_LABEL
  } else {
    label = getLessThanHourLabel(minutes)
  }
  return label
}
